import { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardWrapper, Card, Text, Button } from 'src/shared';
import StatusBadge from 'src/shared/statusBadge';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { getPatients, getAnalysisJobs } from 'src/services/api';
import type { AnalysisJob, PatientPipelineStatus, DisplayStatus } from 'src/types/job';
import { patientAggregateStatus } from 'src/types/job';
import { stripRedundantPatientPrefix } from 'src/utils/patientLabel';
import { ReactComponent as UserAddIcon } from 'src/icons/user-add.svg';

// ---------------------------------------------------------------------------
// Map PatientPipelineStatus to DisplayStatus for badge rendering
// ---------------------------------------------------------------------------
function pipelineToDisplay(s: PatientPipelineStatus): DisplayStatus {
    switch (s) {
        case 'Failed':       return 'Failed';
        case 'Processing':   return 'Processing';
        case 'Queued':       return 'Queued';
        case 'Needs Review': return 'Needs Review';
        case 'Completed':    return 'Completed';
    }
}

// ---------------------------------------------------------------------------
// Patient data shape (from backend /scan/patients)
// ---------------------------------------------------------------------------
interface PatientRow {
    id: number;
    external_id: string;
    display_name: string;
    is_session: boolean;
    last_visit: string | null;
    lastUpload: string | null;
    scanCount: number;
    pipelineStatus: PatientPipelineStatus;
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const Container = styled.div`
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const SubTitle = styled(Text)`
    color: ${Colors.extra.dark_gray};
`;

const MetricsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px;
`;

const MetricCard = styled(Card)`
    padding: 16px;
`;

const MetricTitle = styled(Text)`
    color: ${Colors.extra.dark_gray};
    margin-bottom: 4px;
`;

const MetricValue = styled(Text)`
    color: ${Colors.extra.black};
    font-weight: 600;
`;

const Toolbar = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`;

const SearchInput = styled.input`
    flex: 1;
    height: 40px;
    border: 1px solid ${Colors.theme.sidebar_border};
    border-radius: 8px;
    padding: 0 12px;
    outline: none;
    background: ${Colors.extra.white};
    color: ${Colors.extra.black};
    &:focus {
        border-color: ${Colors.theme.primary};
        box-shadow: 0 0 0 3px rgba(37, 125, 230, 0.1);
    }
`;

const Pills = styled.div`
    display: flex;
    gap: 8px;
`;

const Pill = styled.button<{ $active?: boolean }>`
    border: 1px solid ${Colors.theme.sidebar_border};
    background: ${p => (p.$active ? 'rgba(37,125,230,0.12)' : Colors.extra.white)};
    color: ${p => (p.$active ? Colors.theme.primary : Colors.extra.black)};
    font-weight: ${p => (p.$active ? 600 : 400)};
    padding: 8px 12px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.15s ease;
    &:hover { background: rgba(37,125,230,0.08); }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
    gap: 16px;
`;

const PatientCard = styled(Card)`
    padding: 16px;
`;

const TopRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 12px;
`;

const Identity = styled.div`
    display: grid;
    grid-template-columns: 40px 1fr;
    gap: 12px;
    flex: 1;
    min-width: 0;
`;

const Avatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${Colors.theme.primary}33;
    color: ${Colors.theme.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    flex-shrink: 0;
`;

const Facts = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin: 8px 0 14px 0;
    font-size: 11px;
`;

const Fact = styled.div``;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
`;

type FilterKey = 'all' | 'completed' | 'failed' | 'processing' | 'needs_review';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const PatientsPage: FunctionComponent = () => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<FilterKey>('all');
    const [query, setQuery] = useState('');
    const [patients, setPatients] = useState<PatientRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [showSessions, setShowSessions] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getPatients(1, 100, query || undefined, showSessions);
            const raw: any[] = res?.data?.patients ?? res?.patients ?? [];

            let allJobs: AnalysisJob[] = [];
            try {
                const jobsRes = await getAnalysisJobs({ page: 1, page_size: 1000 });
                allJobs = jobsRes?.results ?? jobsRes?.data ?? [];
            } catch { /* non-critical */ }

            const rows: PatientRow[] = raw.map((p: any) => {
                const extId: string = p.external_id ?? p.full_name ?? `patient-${p.id}`;
                const isSession = p.is_session ?? false;
                const displayNameRaw: string = p.display_name ?? p.external_id ?? p.full_name ?? `Patient ${p.id}`;
                const displayName = stripRedundantPatientPrefix(displayNameRaw, isSession);
                const patientJobs = allJobs.filter(j => j.patient === p.id);
                const status = patientAggregateStatus(patientJobs);
                const uploadDates = patientJobs
                    .map(j => j.created_at)
                    .filter(Boolean)
                    .map(d => new Date(d).getTime())
                    .filter(t => !isNaN(t));
                const lastUpload = uploadDates.length > 0
                    ? new Date(Math.max(...uploadDates)).toISOString()
                    : null;
                return {
                    id: p.id,
                    external_id: extId,
                    display_name: displayName,
                    is_session: isSession,
                    last_visit: p.last_visit ?? null,
                    lastUpload,
                    scanCount: patientJobs.length,
                    pipelineStatus: status,
                };
            });

            setPatients(rows);
        } catch {
            setPatients([]);
        } finally {
            setLoading(false);
        }
    }, [query, showSessions]);

    useEffect(() => { load(); }, [load]);

    const filtered = patients.filter(p => {
        const match = p.external_id.toLowerCase().includes(query.toLowerCase()) || p.display_name.toLowerCase().includes(query.toLowerCase());
        if (!match) return false;
        if (filter === 'completed') return p.pipelineStatus === 'Completed';
        if (filter === 'failed') return p.pipelineStatus === 'Failed';
        if (filter === 'processing') return p.pipelineStatus === 'Processing' || p.pipelineStatus === 'Queued';
        if (filter === 'needs_review') return p.pipelineStatus === 'Needs Review';
        return true;
    });

    const counts = {
        total: patients.length,
        completed: patients.filter(p => p.pipelineStatus === 'Completed').length,
        failed: patients.filter(p => p.pipelineStatus === 'Failed').length,
        inProgress: patients.filter(p => p.pipelineStatus === 'Processing' || p.pipelineStatus === 'Queued').length,
    };

    return (
        <DashboardWrapper>
            <Container>
                <Header>
                    <div>
                        <Text fontSize={Size.Large} fontWeight="600" color={Colors.extra.black}>
                            Patients
                        </Text>
                        <SubTitle fontSize={Size.Small}>
                            Patient records and pipeline status
                        </SubTitle>
                    </div>
                    {/* Add Patient — will be implemented soon
                    <Button>
                        <UserAddIcon style={{ width: 16, height: 16, marginRight: 8, fill: 'currentColor' }} />
                        Add Patient
                    </Button>
                    */}
                </Header>

                <MetricsRow>
                    <MetricCard>
                        <MetricTitle fontSize={Size.XSmall}>Total Patients</MetricTitle>
                        <MetricValue fontSize={Size.Medium}>{counts.total}</MetricValue>
                    </MetricCard>
                    <MetricCard>
                        <MetricTitle fontSize={Size.XSmall}>Completed</MetricTitle>
                        <MetricValue fontSize={Size.Medium}>{counts.completed}</MetricValue>
                    </MetricCard>
                    <MetricCard>
                        <MetricTitle fontSize={Size.XSmall}>Processing</MetricTitle>
                        <MetricValue fontSize={Size.Medium}>{counts.inProgress}</MetricValue>
                    </MetricCard>
                    <MetricCard>
                        <MetricTitle fontSize={Size.XSmall}>Failed</MetricTitle>
                        <MetricValue fontSize={Size.Medium}>{counts.failed}</MetricValue>
                    </MetricCard>
                </MetricsRow>

                <Toolbar>
                    <SearchInput placeholder="Search subjects…" value={query} onChange={e => setQuery(e.target.value)} />
                    <Pills>
                        <Pill $active={filter === 'all'} onClick={() => setFilter('all')}>All</Pill>
                        <Pill $active={filter === 'completed'} onClick={() => setFilter('completed')}>Completed</Pill>
                        <Pill $active={filter === 'processing'} onClick={() => setFilter('processing')}>Processing</Pill>
                        <Pill $active={filter === 'failed'} onClick={() => setFilter('failed')}>Failed</Pill>
                        <Pill $active={filter === 'needs_review'} onClick={() => setFilter('needs_review')}>Needs Review</Pill>
                        <Pill $active={!showSessions} onClick={() => setShowSessions(s => !s)}>
                            {showSessions ? 'Hide Sessions' : 'Show Sessions'}
                        </Pill>
                    </Pills>
                </Toolbar>

                {loading && (
                    <div style={{ padding: 24, textAlign: 'center' }}>
                        <Text>Loading patients…</Text>
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div style={{ padding: 24, textAlign: 'center' }}>
                        <Text color={Colors.extra.dark_gray}>No patients found</Text>
                    </div>
                )}

                {!loading && filtered.length > 0 && (
                    <Grid>
                        {filtered.map(p => {
                            const initials = p.external_id.slice(0, 2).toUpperCase();
                            const prefix = p.is_session ? 'Session' : 'Subject';
                            return (
                                <PatientCard key={p.id}>
                                    <TopRow>
                                        <Identity>
                                            <Avatar>{initials}</Avatar>
                                            <div>
                                                <Text fontWeight="600" color={Colors.extra.black}>{prefix}: {p.display_name}</Text>
                                            </div>
                                        </Identity>
                                        <StatusBadge status={pipelineToDisplay(p.pipelineStatus)} />
                                    </TopRow>

                                    <Facts>
                                        <Fact>
                                            <SubTitle fontSize={Size.XSmall}>Scan Time</SubTitle>
                                            <Text fontSize={Size.XSmall}>
                                                {p.last_visit ? new Date(p.last_visit).toLocaleDateString() : '—'}
                                            </Text>
                                        </Fact>
                                        <Fact>
                                            <SubTitle fontSize={Size.XSmall}>Upload Time</SubTitle>
                                            <Text fontSize={Size.XSmall}>
                                                {p.lastUpload ? new Date(p.lastUpload).toLocaleDateString() : '—'}
                                            </Text>
                                        </Fact>
                                        <Fact>
                                            <SubTitle fontSize={Size.XSmall}>Scans</SubTitle>
                                            <Text fontSize={Size.XSmall}>{p.scanCount || '—'}</Text>
                                        </Fact>
                                        <Fact>
                                            <SubTitle fontSize={Size.XSmall}>Status</SubTitle>
                                            <Text fontSize={Size.XSmall}>{p.pipelineStatus}</Text>
                                        </Fact>
                                    </Facts>

                                    <Footer>
                                        <Button onClick={() => navigate(`/patients/${p.id}`)}>View Details</Button>
                                    </Footer>
                                </PatientCard>
                            );
                        })}
                    </Grid>
                )}
            </Container>
        </DashboardWrapper>
    );
};

export default PatientsPage;
